<script lang="ts">
  import Portal from 'svelte-portal'
  import TimePicker from '../shared/TimePicker.svelte'
  import birthdayStore from '@/stores/birthdays.svelte'
  import groupsStore from '@/stores/groups.svelte'
  import couponsStore from '@/stores/coupons.svelte'
  import { find, pull } from 'lodash'
  import LinkifyingTextarea from '../shared/LinkifyingTextarea.svelte'
  import AttachCouponsList from '../shared/AttachCouponsList.svelte'
  import LinkifyingPhonePreview from '../shared/LinkifyingPhonePreview.svelte'
  import { onMount } from 'svelte'
  import SendUpdateToGroups from './SendUpdateToGroups.svelte'

  let loading = $state(true)
  let birthdaySchedule = $derived(birthdayStore.state.birthdaySchedule)
  let showSendUpdateToGroups = $state(false)

  let allGroup = $derived(groupsStore.allGroup)

  let includeAllContacts = $derived(
    birthdaySchedule?.group_ids?.indexOf(allGroup.id) > -1
  )

  let message = $derived.by(() => {
    let msg = birthdaySchedule.message
    if (selectedCoupon) {
      msg += `\n${selectedCoupon.shortened_url}`
    }
    return msg
  })

  let selectedCoupon = $derived.by(() => {
    if (birthdaySchedule.coupon_id) {
      return find(couponsStore.couponsByName, (coupon) => coupon.id === birthdaySchedule.coupon_id)
    }
  })

  onMount(async () => {
    await birthdayStore.fetch()
    await groupsStore.fetch()
    await couponsStore.fetch()
    loading = false
  })

  function groupClicked(evt, group) {
    let ids = birthdaySchedule.group_ids
    if (evt.target.checked) {
      ids.push(group.id)
    } else {
      ids = pull(ids, group.id)
    }
    birthdaySchedule.group_ids = ids
  }

  function groupIsChecked(id) {
    return birthdaySchedule.group_ids?.indexOf(id) > -1
  }

  function submitForm() {
    let params = { ...birthdaySchedule }
    ;['coupon', 'created_at', 'updated_at'].forEach(key => delete params[key])

    birthdayStore
      .update({
        id: birthdaySchedule.to_param,
        params
      })
      .then(() => {
        toastr.info('Updated!')
      })
  }
</script>

<form
  class="BirthdayScheduleForm birthday-schedule-form"
  class:active={birthdaySchedule.active}
  class:loading={loading}
  onsubmit={() => false}>
  <Portal target="#cta-portal">
    <button class="btn btn-success btn-rounded" onclick={submitForm}>Save</button>
  </Portal>

  <div class="panel shaded">
    <div class="panel-heading">
      <div class="row">
        <div class="col-md-6">
          <label for="birthday-messages">Birthday Messages</label>
          <p>
            Turning this on will allow you to send automatic birthday
            messages to contacts who have provided a birth date.
          </p>
        </div>
        <div class="col-md-6">
          <div class="text-right">
            <div class="switch-wrapper">
              <label class="block" for="birthdays-active">
                Birthdays Active? &nbsp;
              </label>
              <label class="switch">
                <input type="checkbox"
                       bind:checked={birthdaySchedule.active}
                       class="switch-checkbox"
                       id="birthdays-active" />
                <span></span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="panel-body">
      <div class="row">
        <div class="col-md-4">
          <div class="form-group">
            <label for="message">
              Write your message
            </label>

            <LinkifyingTextarea
              id="message"
              placeholder="Happy birthday!"
              coupon={birthdaySchedule.coupon}
              bind:value={birthdaySchedule.message}
            >
              <AttachCouponsList bind:value={birthdaySchedule.coupon_id} coupons={couponsStore.couponsByName} />
            </LinkifyingTextarea>
          </div>
        </div>
        <div class="col-md-4 text-center">
          <label for="phone-preview">Preview</label>
          <LinkifyingPhonePreview
            id="phone-preview"
            text={message}
            class="dark"
          />
        </div>
        <div class="col-md-4">
          <div class="form-group">
            <label for="send-time">
              Time to Send
            </label>
            <TimePicker
              id="send-time"
              placeholder="10:00am"
              bind:value={birthdaySchedule.send_time}
            />

            <p class="mt-1">
              This is the time of day your message will be sent.
            </p>
          </div>
        </div>

      </div>
    </div>
  </div>

  <div class="row mt-2">
    <div class="col-md-6">
      <div class="panel shaded">
        <div class="panel-body">
          <div class="form-group">
            <label for="recipients">Recipients</label>
            <p>
              You can choose to send birthday messages to your whole
              account, or just the selected groups.
            </p>
            <div class="birthday-groups" class:all-groups={includeAllContacts}>

              <ul class="list-group">
                <li class="list-group-item" data-all-group="true">
                  <div class="flex-row">
                    <div class="cell-1">
                      <label class="name-label" for="all-group">
                        Entire Account
                      </label>
                    </div>
                    <div class="cell-0">
                      <div class="text-right">
                        <label class="switch switch--small">
                          <input checked={includeAllContacts} onclick={() => groupClicked(event, allGroup)}
                                 class="switch-checkbox"
                                 type="checkbox" id="all-group" />
                          <span></span>
                        </label>
                      </div>
                    </div>
                  </div>
                </li>

                {#each groupsStore.groupsByName as group (group.id)}
                  <li class="list-group-item">
                    <div class="flex-row">
                      <div class="cell-1">
                        <label class="name-label" for="group-{group.id}">{group.name}</label>
                      </div>
                      <div class="cell-0">
                        <div class="text-right">
                          <label class="switch switch--small">
                            <input
                              checked={groupIsChecked(group.id)}
                              onclick={() => groupClicked(event, group)}
                              class="switch-checkbox"
                              id="group-{group.id}"
                              type="checkbox"
                            />
                            <span></span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </li>
                {/each}

              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="col-md-6">
      <div class="panel shaded">
        <div class="panel-body">
          <label for="update-birthday-info">Update Birthday Info</label>
          <p>
            If contacts are missing their birthdate, you can request
            that they update their profiles.
          </p>
          <div class="help-block">
            NOTE: This will use one credit per recipient.
          </div>
          <button type="button" class="btn btn-success btn-rounded mt-1" onclick={() => showSendUpdateToGroups = true}>
            Select Groups to Update
          </button>
          <SendUpdateToGroups
            show={showSendUpdateToGroups}
            {allGroup}
            groups={groupsStore.groupsByName}
            onClose={() => showSendUpdateToGroups = false} />
        </div>
      </div>
    </div>
  </div>
</form>
